<template>
  <div class="form">
    <Stack gap="4 ">
      <Title>Add Members</Title>

      <div
        v-if="firmStore.isFetching"
        class="loading-zone"
      >
        <LoaderWithText />
      </div>

      <template v-else>
        <div>
          <span>Add as</span>
          <label>
            <input
              v-model="role"
              :disabled="availableAdvisors.length === 0"
              type="radio"
              name="role"
              value="member"
            >
            <RoleBadge role="member" />
          </label>
          <label>
            <input
              v-model="role"
              :disabled="availableAdvisors.length === 0"
              type="radio"
              name="role"
              value="admin"
            >
            <RoleBadge role="admin" />
          </label>
        </div>

        <div
          v-if="availableAdvisors.length"
          class="contact-list"
        >
          <button
            v-for="advisor in availableAdvisors"
            :key="advisor.id"
            class="stk-reset contact-item"
            :disabled="teamsStore.isUpdatingMember.includes(advisor.external_id)"
            @click="addAdvisorToTeam(advisor.external_id)"
          >
            {{ advisor.name }}
          </button>
        </div>

        <div
          v-else
          class="loading-zone"
        >
          <p>
            No advisor left to add
          </p>
        </div>
      </template>

      <Button @click.prevent="emit('onClose', false)">
        Done
      </Button>
    </Stack>
  </div>
</template>

<script setup>
import { computed, defineEmits, onMounted, ref } from 'vue';
import { Button, LoaderWithText, Stack, Title } from '@stakeui';
import { useFirmStore } from '@frontend/ui/stores/firmStore.js';
import { useTeamsStore } from '@frontend/ui/stores/teamsStore.js';
import RoleBadge from './RoleBadge.vue';

const props = defineProps({
  members: {
    type: Array,
    required: true,
  },
  ownerExternalId: {
    type: String,
    required: false,
    default: '',
  },
  teamExternalId: {
    type: String,
    required: true,
  },
});

const firmStore = useFirmStore();
const teamsStore = useTeamsStore();

const emit = defineEmits(['onClose']);

const role = ref('member');

const availableAdvisors = computed(() => {
  if (!firmStore.advisors.length) return [];

  const memberIds = new Set(props.members.map(m => m.external_id));

  if (props.ownerExternalId) {
    memberIds.add(props.ownerExternalId);
  }

  return firmStore.advisors.filter(
      advisor => !memberIds.has(advisor.external_id),
  );
});

const addAdvisorToTeam = async advisorExternalId => {
  try {
    const payload = {
      'advisor_external_id': advisorExternalId,
      'roles': [role.value],
    };
    await teamsStore.addTeamMember(props.teamExternalId, payload);
  } catch (err) {
    console.error(err);
  }
};

onMounted(() => {
  if (firmStore.hasFetched) {
    return;
  }

  firmStore.fetchAdvisors();
});
</script>

<style scoped>
.form {
  padding: 0.5em;
  width: 28rem;
}

.loading-zone,
.contact-list {
  border: 1px solid var(--stk-border);
  border-radius: 0.75em;
  height: 15.5em;
  padding: 0.25em;
  overflow-x: hidden;
}

.loading-zone {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--stk-background);
  flex-direction: column;
  gap: 0.5em;
}

.contact-list {
  overflow-y: scroll;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.contact-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1em;
  padding: 0.25em 0.5em;
  border-radius: 0.5em;
  transition-duration: var(--stk-transition-duration);
  cursor: pointer;
  transition-property: background-color, color, border-color, opacity, outline-color;
  width: 100%;

  &:hover {
    background-color: var(--stk-primary-90);
    color: var(--stk-primary-40);
  }

  &[disabled] {
    opacity: 0.3;
    pointer-events: none;
  }
}
</style>
